import svgPath from 'svgpath';
import { formatSvgPathDecimals } from '@/utils/file-kit';
import { VectorType, type ILayerProps, type IVectorLayerProps } from '@/version-types';
import { checkIsUsingReflection, checkIsUsingShadow } from '../effect';
import { getStylesFromLayer, getSVGGradientsFromLayer, toStringStyle } from '../style';
import { createElementsForReflection, createSVGBlurFilter, createSVGGroup, createSVGUse } from './element';

export class SVGCreator {
    private static svgRoot: HTMLElement;
    private static svgWidth: number;
    private static svgHeight: number;

    private static defs: SVGDefsElement;
    private static clipPath: SVGClipPathElement;

    private static layer: ILayerProps;

    private constructor() {
        throw new Error('SVGCreator is a static class');
    }

    private static scaleSVGPath(path: SVGPathElement, layer: ILayerProps) {
        const { width: layerWidth, height: layerHeight } = layer;
        const { naturalWidth, naturalHeight } = layer.data as IVectorLayerProps;

        const pathValue = path.getAttribute('d')!;
        const pathD = formatSvgPathDecimals(
            svgPath(pathValue)
                .scale(layerWidth / naturalWidth, layerHeight / naturalHeight)
                .toString(),
        );

        path.setAttribute('d', pathD);

        this.svgRoot.setAttribute('viewBox', `0 0 ${layerWidth} ${layerHeight}`);
        this.svgRoot.setAttribute('width', `${layerWidth}`);
        this.svgRoot.setAttribute('height', `${layerHeight}`);
    }

    private static initSVGRootStyle() {
        const originStyle = this.svgRoot.getAttribute('style') || '';
        const nextStyle = `width:100%;height:100%;overflow:visible;${originStyle}`;

        this.svgRoot.setAttribute('style', nextStyle);
    }

    private static initSVGSize() {
        const layerData = this.layer.data as IVectorLayerProps;
        const { naturalWidth, naturalHeight } = layerData;

        this.svgWidth = naturalWidth;
        this.svgHeight = naturalHeight;
    }

    private static initClipPathEl() {
        const clipPathID = `clip-${Math.random().toString(36).slice(2, 11)}`;
        const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');

        clipPath.setAttribute('id', clipPathID);

        this.clipPath = clipPath;
    }

    private static initDefsEl() {
        const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        this.defs = defs;
    }

    private static defineLinearGradient() {
        const gradients = getSVGGradientsFromLayer(this.layer);
        if (!gradients) return;

        this.defs.innerHTML = gradients.content;

        return gradients.id;
    }

    private static defineShape(shapeEl: Element) {
        const originShapeID = `origin-shape_${this.layer.id}`;

        const attributesToRemove = ['stroke', 'fill']; // 要检查的属性数组

        // 不仅要检查 stroke, fill，还要检查 stroke-opacity, fill-opacity 等
        Array.from(shapeEl.attributes).forEach((attr) => {
            if (attributesToRemove.some((key) => attr.name.includes(key))) {
                shapeEl.removeAttribute(attr.name);
            }
        });

        shapeEl.setAttribute('id', originShapeID);

        const layerData = this.layer.data as IVectorLayerProps;
        if (layerData.vectorType !== VectorType.Rect && shapeEl.getAttribute('data-need-clippath') !== 'false') {
            shapeEl.setAttribute('clip-path', `url(#${this.clipPath.id})`);
        }

        this.defs.appendChild(shapeEl);

        return originShapeID;
    }

    private static defineClipPath(shapeEl: Element) {
        const layerData = this.layer.data as IVectorLayerProps;
        const { vectorType } = layerData;

        if (vectorType !== VectorType.Rect && shapeEl.getAttribute('data-need-clippath') !== 'false') {
            this.clipPath.appendChild(shapeEl);
            this.defs.appendChild(this.clipPath);
        }
    }

    private static defineBlurFilter() {
        const layerData = this.layer.data as IVectorLayerProps;
        const { blur } = layerData;

        if (blur <= 0) return;

        const blurID = `blur-filter_${this.layer.id}`;
        const { fill, fillGradient } = layerData;
        const color = fillGradient ? fillGradient : fill;
        const blurFilter = createSVGBlurFilter(blurID, { useFor: 'blur', extent: blur, color });

        this.defs.appendChild(blurFilter);

        return blurID;
    }

    private static createNewDisplayEl(
        shapeID: string,
        shapeEl: Element,
        options: {
            gradientID?: string;
            blurFilterID?: string;
        },
    ) {
        const { gradientID, blurFilterID } = options;

        const layerData = this.layer.data as IVectorLayerProps;
        const { vectorType } = layerData;

        const { fillStyle, strokeStyle } = getStylesFromLayer(this.layer, {
            ratio: vectorType === VectorType.Rect || shapeEl.getAttribute('data-need-clippath') === 'false' ? 1 : 2,
            gradientID,
        });

        const originStyle = shapeEl.getAttribute('style') || '';
        const nextStyle = `${originStyle ? `${originStyle};` : ''}${fillStyle}${strokeStyle}`;

        const useEl = createSVGUse({ href: `#${shapeID}`, style: nextStyle });
        const gEl = blurFilterID ? createSVGGroup({ filter: `url(#${blurFilterID})` }) : null;

        if (gEl) {
            gEl.appendChild(useEl);
            return gEl;
        }

        return useEl;
    }

    private static processShadowEffect(shapeID: string) {
        const layerData = this.layer.data as IVectorLayerProps;
        const isUsingShadow = checkIsUsingShadow(layerData);

        if (!isUsingShadow) return;

        const { id } = this.layer;
        const { effect, vectorType } = layerData;
        const shadow = effect.shadow[0];

        const { blur, color, opacity, x, y } = shadow;
        const blurID = `shadow-blur-filter_${id}`;

        if (blur > 0) {
            const blurFilter = createSVGBlurFilter(blurID, { useFor: 'shadow', extent: blur, color });
            this.defs.appendChild(blurFilter);
        }

        // 由于阴影没有描边，矩形的描边属于居中描边 所以阴影需要设置外描边颜色和宽度填充
        const strokeStyles = (() => {
            if (vectorType !== VectorType.Rect) return {};

            const { strokeWidth } = layerData;
            return { ['stroke-width']: strokeWidth * 2, stroke: color };
        })();

        const finalStyles = {
            transform: `translate(${x}px,${y}px)`,
            opacity: opacity,
            fill: color,
            ...strokeStyles,
        };
        const filterAttr = blur > 0 ? { filter: `url(#${blurID})` } : ({} as Record<string, string>);
        const useEl = createSVGUse({ href: `#${shapeID}`, style: toStringStyle(finalStyles) });
        const gEl = createSVGGroup({ id: `shadow-${id}`, ...filterAttr });

        gEl.appendChild(useEl);

        this.svgRoot.insertBefore(gEl, this.svgRoot.firstChild);
    }

    private static processReflectionEffect(_: string, shapeEl: Element) {
        const layerData = this.layer.data as IVectorLayerProps;
        const isUsingReflection = checkIsUsingReflection(layerData);

        if (!isUsingReflection) return;

        const { id } = this.layer;
        const { effect } = layerData;
        const reflection = effect.reflection[0];

        const { linearGradient, mask } = createElementsForReflection(id, reflection);

        this.defs.appendChild(linearGradient);
        this.defs.appendChild(mask);

        const { blur } = reflection;

        if (blur > 0) {
            const blurFilterID = `reflection-blur-filter_${id}`;
            const blurFilter = createSVGBlurFilter(blurFilterID, { useFor: 'reflection', extent: blur });

            this.defs.appendChild(blurFilter);

            shapeEl.setAttribute('filter', `url(#${blurFilterID})`);
        }

        const parts = this.svgRoot.getAttribute('viewBox')?.trim().split(' ');
        const height = parts?.[parts.length - 1] ?? 0;
        const gEl = createSVGGroup({
            style: `transform:translateY(${reflection.y + 2 * +height}px) scale(1,-1);
                opacity:${reflection.opacity};
            `,
            mask: `url(#${`mask_${id}`})`,
        });

        gEl.appendChild(shapeEl);

        this.svgRoot.insertBefore(gEl, this.svgRoot.firstChild);
    }

    /**
     * 创建简单图形  SVG
     * @param source - 原始 svg 字符串
     * @param layer - 矢量图层
     * ATTENTION: svg中只包含一个路径标签，定义为简单图形
     */
    static create(source: string, layer: ILayerProps) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(source, 'image/svg+xml');

        const parserError = doc.getElementsByTagName('parsererror');
        if (parserError.length > 0) return null; // 解析错误

        this.svgRoot = doc.documentElement;
        this.layer = layer;

        // --- 设置样式 ---
        this.initSVGRootStyle();

        // --- 初始化 svg 大小 ---
        this.initSVGSize();

        const { width: layerWidth, height: layerHeight } = layer;
        const layerData = layer.data as IVectorLayerProps;
        const { vectorType, fillRule = 'nonzero' } = layerData;

        const shapeElements = this.svgRoot.querySelectorAll('path, polygon, rect, ellipse, circle, line, polyline');
        if (shapeElements.length > 0) {
            // --- 渲染路径缩放 ---
            const firstShape = shapeElements[0];
            if (firstShape.tagName.toLowerCase() === 'path' && firstShape.getAttribute('d')) {
                this.scaleSVGPath(firstShape as SVGPathElement, layer);

                this.svgWidth = layerWidth;
                this.svgHeight = layerHeight;
                /** fillRule 目前还没写到配方，暂时写死为evenodd， 后续如果发现问题，需要升级配方 */
                if (vectorType !== VectorType.Rect) this.svgRoot.setAttribute('fill-rule', 'evenodd');
            }

            // --- 设置 clipPath ---
            this.initClipPathEl();

            // --- 设置 defs ---
            this.initDefsEl();

            shapeElements.forEach((el) => {
                const parentNode = el.parentNode as Element;
                const parentTagName = parentNode ? parentNode.tagName.toLowerCase() : '';

                // 排除包裹在 defs 和 clipPath 中的 shape 元素
                if (parentTagName === 'defs' || parentTagName === 'clippath') return;

                // --- defs 相关设置 ---
                // gradient 要先设置，因为 getSVGGradientsFromLayer 返回的是 svg linearGradient 字符串而不是元素
                const gradientID = this.defineLinearGradient();
                const originShapeID = this.defineShape(el.cloneNode(true) as Element);

                this.defineClipPath(el.cloneNode(true) as Element);

                // --- blur 设置 ---
                const blurFilterID = this.defineBlurFilter();

                // 替换原本的应用元素
                const mainEl = this.createNewDisplayEl(originShapeID, el, { gradientID, blurFilterID });
                parentNode.replaceChild(mainEl, el);

                // --- 投影 ---
                this.processShadowEffect(originShapeID);

                // --- 倒影 ---
                this.processReflectionEffect(originShapeID, mainEl.cloneNode(true) as Element);
            });
        }

        this.svgRoot.insertBefore(this.defs, this.svgRoot.firstChild);

        const serializer = new XMLSerializer();
        const str = serializer.serializeToString(doc);

        return {
            svgString: str.replace(/xmlns=""/g, ''),
            svgWidth: this.svgWidth,
            svgHeight: this.svgHeight,
        };
    }
}
